/**
 * **추적 세 갈래가 플랜 안인가** — 브랜드 키워드(브랜드 멘션 추적) · 자사 계정 · 타겟 키워드(타겟 멘션 추적).
 *
 * 🔑 판정은 하나다 — 워크스페이스 허용값(`brandYN` · `keywordYN`)이 참이 아니거나 그 갈래의 플랜 한도가 0 이면 플랜 밖이다
 *   (`isPlanTrackAreaLocked`). 백엔드가 알림을 거르는 잣대(허용값)와 같은 답을 낸다 — 맞춤 플랜이 있어 플랜 이름으로 비교하지 않는다.
 *   추적 관리(`sections/status`)와 홈(`sections/find`)이 이 한 벌을 함께 쓴다 — 판정 사본을 만들지 마라.
 * ⚠️ «다 쓴»(한도 = 사용량) 것은 플랜 안이다 — 플랜 밖은 한도 0 일 때만이다.
 * ⚠️ 순수 함수다 — `'use client'` 를 붙이지 않는다.
 */

import type { PlanConfig } from 'src/types/plan/plan-domain';

import { resolvePlanServiceValue } from 'src/utils/plan/plan-access';

import { IPlanService } from 'src/constants/plan-service';

/** 추적 갈래 열쇠 — 추적 관리의 구역 열쇠와 같다 */
export type PlanTrackKey = 'brandKeyword' | 'brandAccount' | 'targetKeyword';

/** 갈래마다 한도를 읽는 플랜 표의 칸 */
export const PLAN_TRACK_LIMIT_FIELD: Record<PlanTrackKey, string> = {
  brandKeyword: IPlanService.brand.brandKeywordCount,
  brandAccount: IPlanService.brandAccount.brandAccountCount,
  targetKeyword: IPlanService.keyword.keywordCount,
};

/**
 * 그 갈래의 플랜 한도.
 * ⚠️ 표에 칸이 없으면 0 이다 — 그 플랜은 그 갈래를 안 준다(INDIVIDUAL 의 브랜드 키워드).
 * @returns 플랜 표가 없거나 숫자로 안 읽히면 `null`(모르는 것과 0 은 다르다)
 */
export function toPlanTrackLimit(config: PlanConfig | null | undefined, key: PlanTrackKey): number | null {
  if (!config) return null;
  const value = Number(resolvePlanServiceValue(config, PLAN_TRACK_LIMIT_FIELD[key]) ?? 0);
  return Number.isFinite(value) ? value : null;
}

/** 플랜 밖 판정 — 한도가 0 일 때만이다(「다 쓴」 것은 플랜 안이다) */
export function isPlanTrackLocked(limit: number | null): boolean {
  return limit === 0;
}

/** 갈래마다 허용값을 읽는 플랜 표의 칸 — 브랜드 키워드·자사 계정은 `brandYN`, 타겟 키워드는 `keywordYN` */
export const PLAN_TRACK_ALLOW_FIELD: Record<PlanTrackKey, string> = {
  brandKeyword: IPlanService.brand.brandYN,
  brandAccount: IPlanService.brand.brandYN,
  targetKeyword: IPlanService.keyword.keywordYN,
};

/**
 * 그 갈래가 플랜 밖인가 — 허용값이 참이 아니거나 한도가 0.
 * ⚠️ 플랜 표가 없으면(로그인 전 · 플랜을 못 읽음) `false` — 모르는 것을 잠그지 않는다(관문은 라우트가 한다).
 */
export function isPlanTrackAreaLocked(config: PlanConfig | null | undefined, key: PlanTrackKey): boolean {
  if (!config) return false;
  if (resolvePlanServiceValue(config, PLAN_TRACK_ALLOW_FIELD[key]) !== true) return true;
  return isPlanTrackLocked(toPlanTrackLimit(config, key));
}
